import { useEffect, useRef, useState, useCallback, type ReactElement } from 'react';
import { TypedLines, AdvanceBubble, useRevealFallback, fetchObjects, pick, isDemoSourced, eventStartTs, TOPIC_LABEL, initials } from './onboarding-beats-shared';
import type { AssessRow, NetContact, Bundle, BeatPayload, BeatContext } from './onboarding-beats-shared';
export type { BeatKind, AssessRow, NetContact, Bundle, BeatPayload, BeatContext } from './onboarding-beats-shared';
import { resolveStr } from './onboarding-contact-shape';
import { apiFetch } from '@/lib/apiClient';
import { listIntegrations } from '@/lib/api/endpoints/integrations';
import { getSeatUsage, type SeatUsage } from '@/lib/api/endpoints/spaces';
import { emailApiBase, getHeaders } from '@/lib/email-api';
import { getCachedInbox } from '@/lib/api/endpoints/email-module';
import { calendarFlash } from '@/lib/calendarFlash';
import { useDemoData } from '@/features/demo/useDemoMode';
import { BriefingPanel } from '@/features/briefing/BriefingPanel';
import { OnboardingAnalysisBeat } from './OnboardingAnalysisBeat';
import { OnboardingCommandsBeat } from './OnboardingCommandsBeat';
import { EmailResultCard } from './result-cards/EmailResultCard';
import { EmailDraftCard } from './result-cards/EmailDraftCard';
import { EventsResultCard } from './result-cards/EventsResultCard';
import { ContactDetailCard } from './result-cards/ContactDetailCard';
import { NoteResultCard } from './result-cards/NoteResultCard';
import type { EmailCardData, EmailDraftCardData, EventsCardData, EventsCardEvent, ContactCardData, NoteCardData } from './result-cards/types';
import './result-cards/result-cards.css';
import './OnboardingRevealCard.css';

/**
 * Onboarding-as-transcript. Each beat (welcome → ask → connect → scan →
 * assessment → calendar → contacts → demo → places) is a REAL assistant
 * chat message: its content is ONBOARDING_BEAT_MARKER + JSON(BeatPayload), which
 * ChatBox detects and renders as <OnboardingBeatCard>. Because they're real
 * messages they accumulate into one persisted transcript that survives the OAuth
 * round-trip, completion, and a reload — the onboarding IS the chat.
 *
 * The driver (ChatBox) appends the next beat when the user advances; only the
 * latest beat shows its action button (`isCurrent`). Post-scan beats carry the
 * derived `bundle` so each message is self-contained.
 */

export const ONBOARDING_BEAT_MARKER = '@@XAVIOR_ONBOARDING_BEAT@@';


export function encodeBeat(p: BeatPayload): string {
  return ONBOARDING_BEAT_MARKER + JSON.stringify(p);
}
export function parseBeat(content: string): BeatPayload | null {
  if (!content.startsWith(ONBOARDING_BEAT_MARKER)) return null;
  try { return JSON.parse(content.slice(ONBOARDING_BEAT_MARKER.length)) as BeatPayload; }
  catch { return null; }
}

// ── scan/discovery shapes (mirrors the old reveal card) ──
interface Person { name: string; email: string; sentCount: number; twoWay: boolean }
interface Job {
  status: 'pending' | 'running' | 'done' | 'failed' | 'deferred';
  emailCount?: number; entities?: string[]; financialInstitutions?: string[];
  contactCount?: number; peopleCandidates?: Person[];
  sentTopics?: Array<{ topic: string; count: number }>;
  pastCount?: number; upcomingCount?: number; projects?: string[];
  travelTrips?: Array<{ destination: string; dateRange: string }>;
  recurringMeetings?: Array<{ title: string; count: number }>;
  revealText?: string;
}
interface DiscoveryStatus { jobs: { inbox: Job; sent: Job; calendar: Job; letter: Job }; allDone: boolean }

const POLL_MS = 1_300;
const TERMINAL = new Set(['done', 'failed', 'deferred']);
const PALETTE = ['#1f6f5c', '#3a6ea5', '#b4763f', '#7a5ea8', '#4f8a6b', '#a8546a', '#5b8fb0', '#8a7a3f'];
function hueFor(email: string): string {
  let h = 0; for (const c of email) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return PALETTE[h % PALETTE.length];
}

const AICON: Record<string, ReactElement> = {
  loc: <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>,
  brief: <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /></svg>,
  people: <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>,
  flag: <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" /><line x1="4" y1="22" x2="4" y2="15" /></svg>,
  clock: <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>,
  recur: <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" /></svg>,
  cal: <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>,
  food: <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 2v7c0 1.1.9 2 2 2s2-.9 2-2V2" /><line x1="5" y1="11" x2="5" y2="22" /><path d="M19 15V2a4 4 0 0 0-3 9v11" /></svg>,
  health: <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78z" /></svg>,
};

/** True when this onboarding should use the scripted Boris demo persona. */
export function isBorisPersona(userEmail?: string | null, isDemo?: boolean): boolean {
  return (userEmail ?? '').trim().toLowerCase() === 'marcus@northwind.ai' || !!isDemo;
}

/** Derive the post-scan bundle: Boris persona gets the scripted read, others derive. */
export function deriveBundle(
  status: DiscoveryStatus | null,
  userEmail?: string | null,
  userName?: string | null,
  isDemo?: boolean,
): Bundle {
  const sent = status?.jobs.sent, cal = status?.jobs.calendar, inbox = status?.jobs.inbox;
  const people = (sent?.peopleCandidates ?? []).slice(0, 6);
  const topics = (sent?.sentTopics ?? []).map((t) => TOPIC_LABEL[t.topic] ?? t.topic).slice(0, 4);
  const boris = isBorisPersona(userEmail, isDemo);

  const name = boris ? 'Marcus Hale' : (userName?.trim() || (userEmail ? userEmail.split('@')[0] : 'You'));
  const companyRoot = ((userEmail ?? '').split('@')[1] ?? '').split('.')[0];
  const company = boris ? 'Northwind AI' : (companyRoot ? companyRoot.charAt(0).toUpperCase() + companyRoot.slice(1) : '');
  const initial = (name || '?').charAt(0).toUpperCase();
  const topContact = boris ? 'Nina' : (people[0]?.name?.trim() || people[0]?.email?.split('@')[0] || 'a key contact');

  let assess: AssessRow[];
  let assessSections: Array<{ title: string; body: string }>;
  let calRows: AssessRow[];
  let calClosingBold: string;
  let contacts: NetContact[];

  if (boris) {
    assess = [
      { icon: 'loc', text: 'Based in San Francisco — you work on Pacific time.' },
      { icon: 'brief', text: "Your work orbits Northwind — all-hands, board prep, and a 1:1 with the CEO are on your calendar." },
      { icon: 'people', text: 'You email Nina, Daniel, and Oscar more than anyone — you can tell me more about their roles later.' },
      { icon: 'flag', text: 'A couple of VIP threads — Reed Calloway and Mira Vale — are waiting on a reply from you.', bold: true },
      { icon: 'clock', text: 'You guard time for therapy, weekly reviews, and a long run at Crissy Field.' },
    ];
    assessSections = [
      { title: 'Who you are', body: "You're an engineer embedded in the AI world — someone who builds, and who follows the industry closely enough that the news doubles as professional development. Your work centers on product and engineering, with a secondary pull toward deals and partnerships, which says you're not purely heads-down. The threads on your plate right now — therapy, team retros, long runs — sketch someone who takes recovery seriously, in more than one dimension." },
      { title: 'How you operate', body: "You're running several efforts in parallel, with intention, not as a single-track employee. Your calendar is busy but not chaotic, and you haven't carved out formal protected blocks yet — which is either discipline, or an opportunity." },
    ];
    calRows = [
      { icon: 'recur', text: 'Recurring — therapy with Dr. Reyes, a weekly team retro, and a solo weekly review.' },
      { icon: 'cal', text: 'Northwind — all-hands, Q2 board prep, and a 1:1 with the CEO.' },
      { icon: 'loc', text: 'Travel — Frontier Summit week (prep, keynote, afterparty) and a Tahoe family ski weekend.' },
      { icon: 'food', text: 'Dinners — a rival-lab CEO, an old friend, and a customer dinner around the Summit.' },
      { icon: 'health', text: 'Personal — long runs at Crissy Field, and your anniversary with Nadia is coming up.' },
    ];
    calClosingBold = 'Patterns like these are how I learn you: you protect therapy and your long runs, your weeks orbit Northwind, and family is on the calendar too. So I plan around your life, not over it. ';
    contacts = [
      { name: 'Daniel Okafor', email: 'daniel@northwind.ai', note: "Engineer at Northwind. Quietly preparing to give notice — told you Friday is his last day and he's headed to a competitor. Asked you to keep it between you two for now.", aiEnhanced: true },
      { name: 'Nina Castellanos', email: 'nina@northwind.ai', note: 'Head of Finance at Northwind. Flagged a discrepancy in the Q2 numbers and wants a quiet 15-minute sync off-site (Sightglass) before it goes to the board.', aiEnhanced: true },
      { name: 'Oscar Blake', email: 'oscar@northwind.ai', note: 'Product at Northwind. Heads-up he gave you: a sensitive issue from the leadership offsite is likely to land on your desk this week. Wants to talk before it does.', aiEnhanced: true },
      { name: 'Reed Calloway', email: 'reed@vantage.co', note: 'Founder of Vantage and Orbital. Uses you as a sounding board before posting anything spicy, and during timing-sensitive announcements. High signal, low patience.', aiEnhanced: true },
      { name: 'Mira Vale', email: 'mira@miravale.com', note: 'Musician, close personal friend going back years. Keeping some personal news quiet through the album cycle — assume nothing she tells you is public yet.', aiEnhanced: true },
      { name: 'Hari Menon', email: 'hari@swiftly.com', note: "Board director and prolific connector — the best founder-introducer you know. Remembers exactly what stage everyone's at. Worth a warm note before you need a favor.", aiEnhanced: true },
    ];
  } else {
    assess = [];
    assessSections = []; // derived path keeps the compact icon-row read for now
    if (topics.length > 0) assess.push({ icon: 'brief', text: `Most of your threads are about ${topics.slice(0, 2).join(' and ')}.` });
    const team = people.slice(0, 3).map((p) => (p.name?.trim() || p.email.split('@')[0])).filter(Boolean);
    if (team.length > 0) {
      const list = team.length > 1 ? `${team.slice(0, -1).join(', ')} and ${team[team.length - 1]}` : team[0];
      assess.push({ icon: 'people', text: `You email ${list} more than anyone — you can tell me more about their roles later.` });
    }
    calRows = [];
    const recurring = (cal?.recurringMeetings ?? []).slice(0, 2).map((m) => m.title).filter(Boolean);
    if (recurring.length > 0) calRows.push({ icon: 'recur', text: `Recurring — ${recurring.join(', ')}.` });
    const trip = (cal?.travelTrips ?? [])[0];
    if (trip?.destination) calRows.push({ icon: 'loc', text: `Travel — ${trip.destination}${trip.dateRange ? `, ${trip.dateRange}` : ''}.` });
    const upcoming = cal?.upcomingCount ?? 0;
    if (upcoming > 0) calRows.push({ icon: 'cal', text: `Meetings — ${upcoming} coming up this week.` });
    calClosingBold = 'Patterns like these are how I learn your rhythm. ';
    contacts = people.slice(0, 6).map((p) => ({
      name: p.name?.trim() || p.email.split('@')[0],
      email: p.email,
      note: p.twoWay ? 'You write back and forth.' : `${p.sentCount} email${p.sentCount === 1 ? '' : 's'} between you.`,
    }));
  }
  // keep counts handy for any future use (unused refs avoided)
  void inbox;
  const subject = boris ? name : 'you';
  const possessive = boris ? `${name}'s` : 'your';
  return { name, company, initial, topContact, third: boris, subject, possessive, assessSections, assess, calRows, calClosingBold, contacts };
}

// ── small presentational helpers ──
function Lane({ icon, label, unit, count, done }: { icon: string; label: string; unit: string; count: number; done: boolean }) {
  return (
    <div className={`obr-lane${done ? ' done' : ''}`}>
      <span className="obr-lane-ic">{done ? '✓' : <span className="obr-spin" />}</span>
      <span className="obr-lane-label">{icon} {label}</span>
      <span className="obr-lane-count">{done ? <>{count.toLocaleString()} <small>{unit}</small></> : <span className="obr-dim">reading…</span>}</span>
    </div>
  );
}

// The user-bubble "question" shown on each skill demo that advances to the next.
// Demo uses the scripted Marcus cast; real uses generic questions that match
// whatever the next demo surfaces from the user's actual synced data.
const DEMO_NEXT_LABEL: Record<string, string> = {
  agents: 'What was the email from Mira Vale?', email: "When's my next therapist appointment?", calendar: 'Who does Marcus email most?', contacts: 'Find the candidate doc', drive: "Okay, you've made your point",
};
const REAL_NEXT_LABEL: Record<string, string> = {
  agents: "What's my latest email?", email: "What's on my calendar this week?", calendar: 'Who do I email most?', contacts: 'What have you saved about me?', drive: "Got it — what's next?",
};
const DEMO_ORDER: Array<'agents' | 'email' | 'calendar' | 'contacts' | 'drive'> = ['agents', 'email', 'calendar', 'contacts', 'drive'];


// Marks the document while ANY onboarding beat is mounted, so far-away UI
// (e.g. the contact card's AI Enhance button, opened through the chat modal
// layer) can style itself for onboarding without prop-drilling through the
// ChatBox dispatch chain. Ref-counted — multiple beats are mounted at once.
let onboardingBeatMounts = 0;

/** Render ONE onboarding beat (its content comes from a persisted chat message). */
export function OnboardingBeatCard({ payload, ctx }: { payload: BeatPayload; ctx: BeatContext }) {
  useEffect(() => {
    onboardingBeatMounts++;
    document.body.dataset.onboardingBeats = '1';
    return () => {
      onboardingBeatMounts--;
      if (onboardingBeatMounts <= 0) delete document.body.dataset.onboardingBeats;
    };
  }, []);
  switch (payload.kind) {
    case 'team-welcome': return <TeamWelcomeBeat ctx={ctx} />;
    case 'team-owner-welcome': return <TeamOwnerOnboardingBeat ctx={ctx} />;
    case 'welcome': return <WelcomeBeat ctx={ctx} />;
    case 'ask': return <AskBeat ctx={ctx} />;
    case 'demo-intro': return <DemoIntroBeat ctx={ctx} />;
    case 'scan': return <ScanBeat ctx={ctx} />;
    case 'assessment': return <AssessmentBeat payload={payload} ctx={ctx} />;
    case 'calendar': return <CalendarBeat payload={payload} ctx={ctx} />;
    case 'contacts': return <ContactsBeat payload={payload} ctx={ctx} />;
    case 'demo': return <DemoBeat payload={payload} ctx={ctx} />;
    case 'pages': return <PagesBeat payload={payload} ctx={ctx} />;
    case 'walkthrough': return <WalkthroughBeat payload={payload} ctx={ctx} />;
    case 'analysis': return <OnboardingAnalysisBeat payload={payload} ctx={ctx} />;
    case 'commands': return <OnboardingCommandsBeat payload={payload} ctx={ctx} />;
    default: return null;
  }
}

/** Advance control: a green "Next" button on the live beat. Clicking it advances —
 *  and because the beat is then past, its blue user chat bubble renders in its place
 *  (.oa-userbubble--sent). So clicking Next pops the user's message into the chat. */
// TypedLines, useRevealFallback, AdvanceBubble — imported from onboarding-beats-shared

// ── Team-member onboarding beat (lightweight, no demo transcript) ──────────────
function teamWelcomeLines(name: string | null | undefined): string[] {
  return [
    name ? `Welcome to ${name}.` : 'Welcome to your team.',
    "Connect your Gmail, Calendar & Drive and I'll get to work for you here.",
    "Your email stays private — only what you choose to share is visible to the team.",
  ];
}

function TeamWelcomeBeat({ ctx }: { ctx: BeatContext }) {
  const [done, setDone] = useState(!ctx.isCurrent);
  // Derive lines once on mount — workspaceName won't change mid-beat.
  const [lines] = useState(() => teamWelcomeLines(ctx.workspaceName));

  const handleConnect = () => {
    // Open the OAuth popup synchronously (user gesture — must not await first).
    // Completion of member onboarding is handled by connectBeat in ChatBox after
    // the popup resolves successfully, so refreshSession() never races the open
    // popup and the onboarding UI stays mounted for the full OAuth round-trip.
    void ctx.connect('oauth.google.email');
  };

  // Track skip so the echoed chat bubble reflects the SKIP, not the connect
  // label — AdvanceBubble auto-renders its label as a "sent" bubble once the
  // beat is past, which otherwise wrongly read "Connect my accounts" after skip.
  const [skipped, setSkipped] = useState(false);

  const handleSkip = () => {
    setSkipped(true);
    ctx.onComplete();
  };

  return (
    <div className="oa-welcome">
      <TypedLines active={ctx.isCurrent} lines={lines} onComplete={() => setDone(true)} />
      {done && (
        <>
          {skipped ? (
            <div className="oa-userbubble-row">
              <div className="oa-userbubble oa-userbubble--sent">I&rsquo;ll skip adding my accounts for now</div>
            </div>
          ) : (
            <AdvanceBubble label="Connect my accounts" isCurrent={ctx.isCurrent} onAdvance={handleConnect} />
          )}
          {ctx.isCurrent && !skipped && (
            <button type="button" className="obr-link-btn" onClick={handleSkip}>
              Skip for now
            </button>
          )}
        </>
      )}
    </div>
  );
}

// ── Team-OWNER onboarding beat: a short setup checklist (connect → invite → seats) ──

/** Seat summary line for the team-owner beat: owner occupies one seat, so the
 *  invitable count is seatCap − 1. Singular "teammate" at exactly 2 seats. */
export function seatLine(seat: SeatUsage | null): string {
  if (!seat) return 'Checking your plan…';
  if (seat.seatCap <= 1) return "You've got 1 seat — just you for now.";
  const others = Math.max(0, seat.seatCap - 1);
  const noun = others === 1 ? 'teammate' : 'teammates';
  return `You've got ${seat.seatCap} seats — that's you plus ${others} ${noun}.`;
}

/** Which connected provider to acknowledge in the greeting — Google preferred
 *  when both are present. Returns null when neither is actively connected. */
export function connectedProviderLabel(
  integrations: Array<{ provider: string; status: string }>,
): 'Google' | 'Microsoft' | null {
  const active = (p: string) => integrations.some((i) => i.provider === p && i.status === 'active');
  if (active('google')) return 'Google';
  if (active('microsoft')) return 'Microsoft';
  return null;
}

function teamOwnerLines(name: string | null | undefined): string[] {
  return [
    name ? `Welcome to ${name} — your new team workspace.` : 'Welcome to your new team workspace.',
    'Two quick things and your team is ready: connect an account, and invite your people.',
  ];
}

function TeamOwnerOnboardingBeat({ ctx }: { ctx: BeatContext }) {
  const [done, setDone] = useState(!ctx.isCurrent);
  const [lines] = useState(() => teamOwnerLines(ctx.workspaceName));
  const [provider, setProvider] = useState<'Google' | 'Microsoft' | null>(null);
  const [seat, setSeat] = useState<SeatUsage | null>(null);

  // Pull connection status + seat usage; re-pull on window focus so connecting an
  // account (OAuth popup) or sending an invite (Team modal) reflects back into the
  // checklist without a manual refresh.
  const refresh = useCallback(async () => {
    const token = ctx.token;
    if (!token) return;
    try {
      const integrations = await listIntegrations(token, { status: 'active' });
      setProvider(connectedProviderLabel(integrations));
    } catch { /* keep prior state */ }
    try {
      if (ctx.workspaceId) setSeat(await getSeatUsage(token, ctx.workspaceId));
    } catch { /* keep prior state */ }
  }, [ctx.token, ctx.workspaceId]);

  useEffect(() => { void refresh(); }, [refresh]);
  useEffect(() => {
    const onFocus = () => { void refresh(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refresh]);

  const invitesSent = seat !== null && (seat.pendingInvites > 0 || seat.memberCount > 1);

  return (
    <div className="oa-welcome">
      <TypedLines active={ctx.isCurrent} lines={lines} onComplete={() => setDone(true)} />
      {done && (
        <div className="oa-team-checklist">
          {/* ① Connect a provider */}
          <div className="oa-team-step">
            <span className={`oa-team-step-mark${provider ? ' is-done' : ''}`}>{provider ? '✓' : '1'}</span>
            <div className="oa-team-step-body">
              <div className="oa-team-step-title">Connect Google or Microsoft</div>
              {provider ? (
                <div className="oa-team-step-sub">You've connected {provider} ✓ — your inbox stays private to you.</div>
              ) : ctx.isCurrent ? (
                <div className="oa-team-step-actions">
                  <button type="button" className="obr-action-btn" onClick={() => void ctx.connect('oauth.google.email')}>Connect Google</button>
                  <button type="button" className="obr-action-btn obr-action-btn--ghost" onClick={() => void ctx.connect('oauth.microsoft.outlook')}>Connect Microsoft</button>
                </div>
              ) : (
                <div className="oa-team-step-sub">Connect from the workspace anytime.</div>
              )}
            </div>
          </div>
          {/* ② Invite the team */}
          <div className="oa-team-step">
            <span className={`oa-team-step-mark${invitesSent ? ' is-done' : ''}`}>{invitesSent ? '✓' : '2'}</span>
            <div className="oa-team-step-body">
              <div className="oa-team-step-title">Invite your team</div>
              {invitesSent ? (
                <div className="oa-team-step-sub">{seat?.pendingInvites ?? 0} invited · {seat?.memberCount ?? 1} on the team.</div>
              ) : ctx.isCurrent ? (
                <div className="oa-team-step-actions">
                  <button type="button" className="obr-action-btn" onClick={() => ctx.onOpenTeam?.()}>Invite teammates</button>
                </div>
              ) : (
                <div className="oa-team-step-sub">Invite teammates from the Team panel.</div>
              )}
            </div>
          </div>
          {/* ③ Seats */}
          <div className="oa-team-step">
            <span className="oa-team-step-mark">3</span>
            <div className="oa-team-step-body">
              <div className="oa-team-step-title">Your seats</div>
              <div className="oa-team-step-sub">{seatLine(seat)}</div>
            </div>
          </div>
          {ctx.isCurrent && (
            <AdvanceBubble label="Enter my workspace" isCurrent={ctx.isCurrent} onAdvance={() => ctx.onComplete()} />
          )}
        </div>
      )}
    </div>
  );
}

const WELCOME_LINES = [
  "Hi. I'm Xavior.",
  "I am going to spend a few minutes showing you what I can do. Ready?",
];

function WelcomeBeat({ ctx }: { ctx: BeatContext }) {
  const [done, setDone] = useState(!ctx.isCurrent);
  return (
    <div className="oa-welcome">
      <TypedLines active={ctx.isCurrent} lines={WELCOME_LINES} onComplete={() => setDone(true)} />
      {done && <AdvanceBubble label="Ready" isCurrent={ctx.isCurrent} onAdvance={() => ctx.advance('ask')} />}
    </div>
  );
}

const ASK_LINES = [
  "To start, you need to connect your email. I won't do or send anything without your permission, and your data is kept segregated and private.",
];

// Connect beat — the decision point. On mount it checks whether a real account
// is ALREADY connected (via the email-status endpoint, which reads the canonical
// provider_integrations and excludes demo). If so it skips the prompt and runs
// the personalized walkthrough on real data. Otherwise it offers to connect
// (real = default) with a sample-data fallback.
function AskBeat({ ctx }: { ctx: BeatContext }) {
  const [done, setDone] = useState(!ctx.isCurrent);
  // null = checking, true = already synced (auto-real), false = needs a choice.
  const [alreadySynced, setAlreadySynced] = useState<boolean | null>(ctx.isCurrent ? null : false);

  // No cross-invocation guard ref here: React StrictMode mounts → unmounts →
  // remounts in dev, and a ref-based "run once" guard would let the FINAL mount
  // skip the check while the first mount's result was already discarded (its
  // `alive` flag was flipped on cleanup) — leaving `alreadySynced` null forever
  // (stuck). The per-invocation `alive` flag is the correct StrictMode-safe guard.
  useEffect(() => {
    if (!ctx.isCurrent) return;
    let alive = true;
    void (async () => {
      try {
        // Use the email-status endpoint with the beat's EXPLICIT workspaceId — it
        // reads provider_integrations (the source of truth), unlike GET /integrations
        // (the legacy table, which can be empty for a freshly-OAuth'd account).
        // IMPORTANT: the endpoint INCLUDES demo integrations in `accounts` (their
        // seeded mailbox renders in the Email module), so `connected` is true even
        // for a demo-only workspace. We must check the accounts list for a REAL
        // (non-demo) valid account, or sampling the demo would later be mistaken
        // for a real sync and skip the connect prompt.
        const res = ctx.workspaceId
          ? await apiFetch<{ connected?: boolean; accounts?: Array<{ id: string; tokenValid?: boolean }> }>(
              `/workspaces/${ctx.workspaceId}/integrations/email/status`,
              { authToken: ctx.token ?? undefined, headers: { 'X-Workspace-Id': ctx.workspaceId } },
            )
          : null;
        if (!alive) return;
        const realConnected = (res?.accounts ?? []).some((a) => a.tokenValid && !a.id.startsWith('demo-integration-'));
        if (realConnected) {
          setAlreadySynced(true);
          // Already connected: skip the prompt, run the real personalized path.
          ctx.connect('use-existing');
          return;
        }
      } catch { /* fall through to the choice */ }
      if (alive) setAlreadySynced(false);
    })();
    // Safety: never strand the user on a silent check — if it hasn't resolved
    // shortly, fall back to the connect choice so a button always appears.
    const t = setTimeout(() => { if (alive) setAlreadySynced((v) => (v === null ? false : v)); }, 4000);
    return () => { alive = false; clearTimeout(t); };
  }, [ctx.isCurrent]);

  // Past beat (the connect decision is already made — the next beats reflect it):
  // never re-render the connect prompt with its now-stale, disabled buttons. On the
  // real path show a brief acknowledgement; on the demo path the demo-intro beat
  // speaks for itself, so render nothing. (Detection only runs while current, so a
  // past beat's `alreadySynced` is false and would otherwise fall through to the
  // connect prompt — which is exactly what left the prompt stuck on screen.)
  if (!ctx.isCurrent) {
    // Real path: silent — the analysis beat opens the story ("First thing,
    // I am going to read your calendar…"); an acknowledgement line here just
    // delayed it. Demo speaks via the demo-intro beat.
    return null;
  }

  // While the sync check is still running (current beat only), render nothing —
  // never flash the connect prompt before we know whether the account is already
  // connected. Resolves in well under a second; the welcome beat stays on screen.
  if (alreadySynced === null) return null;

  // Already connected: render nothing — the scan starts silently and the
  // analysis beat opens the story.
  if (alreadySynced === true) return null;

  return (
    <div className="oa-welcome">
      <TypedLines active={ctx.isCurrent} lines={ASK_LINES} onComplete={() => setDone(true)} />
      {/* Reaching this render means we're NOT already-synced (that case returned
          above), so once the intro finishes typing the choice always appears —
          the user can never be stranded with the input locked and no button. */}
      {done && (
        <>
          <div className="oa-source-stack">
            <button className="oa-src-btn oa-src-btn--google" disabled={!ctx.isCurrent} onClick={() => ctx.connect('oauth.google.email')}>
              <svg className="oa-src-ic" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path fill="#fff" d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z" /></svg>
              <span>Connect Google</span>
            </button>
            <button className="oa-src-btn oa-src-btn--microsoft" disabled={!ctx.isCurrent} onClick={() => ctx.connect('oauth.microsoft.outlook')}>
              <svg className="oa-src-ic" viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true"><rect x="3" y="3" width="8" height="8" rx="1" /><rect x="13" y="3" width="8" height="8" rx="1" /><rect x="3" y="13" width="8" height="8" rx="1" /><rect x="13" y="13" width="8" height="8" rx="1" /></svg>
              <span>Connect Microsoft</span>
            </button>
          </div>
          <button type="button" className="oa-skip-link" disabled={!ctx.isCurrent} onClick={() => ctx.connect('demo')}>Not now — show me a sample first</button>
        </>
      )}
    </div>
  );
}

const DEMO_INTRO_LINES = [
  "Not ready to hand over the keys? Fair. But you're really going to make me work with made-up data?",
  "Fine. Since you won't show me yours, I'll show you what I'd do with someone else's.",
  "Meet Marcus Hale — an engineer at Northwind AI. Watch closely. Every single thing I'm about to do with his world, I'd be doing with yours.",
];

function DemoIntroBeat({ ctx }: { ctx: BeatContext }) {
  // The sassy opener for the fallback (declined-sync) path. Stays in the
  // transcript as the demo marker (#3509 relies on a demo-intro beat).
  const [done, setDone] = useState(!ctx.isCurrent);
  return (
    <div className="oa-welcome">
      <TypedLines active={ctx.isCurrent} lines={DEMO_INTRO_LINES} onComplete={() => setDone(true)} />
      {done && <AdvanceBubble label="Alright, show me what you've got" isCurrent={ctx.isCurrent} onAdvance={() => ctx.advance('scan')} />}
    </div>
  );
}

// Minimum reveal time per lane (ms) so the scan *feels* like a live Google pull
// even when the demo data is already seeded server-side. A lane only flips to
// "done" once its real job has finished AND its reveal time has elapsed.
const LANE_REVEAL_MS = { cal: 3200, sent: 5800, inbox: 8600 };

function ScanBeat({ ctx }: { ctx: BeatContext }) {
  const [status, setStatus] = useState<DiscoveryStatus | null>(null);
  const [revealed, setRevealed] = useState({ cal: false, sent: false, inbox: false });
  const retriedRef = useRef(false);
  const stallRef = useRef(0);
  useEffect(() => {
    const t1 = setTimeout(() => setRevealed((r) => ({ ...r, cal: true })), LANE_REVEAL_MS.cal);
    const t2 = setTimeout(() => setRevealed((r) => ({ ...r, sent: true })), LANE_REVEAL_MS.sent);
    const t3 = setTimeout(() => setRevealed((r) => ({ ...r, inbox: true })), LANE_REVEAL_MS.inbox);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, []);
  useEffect(() => {
    if (!ctx.workspaceId) return;
    let alive = true;
    const poll = async () => {
      try {
        const data = await apiFetch<DiscoveryStatus>(`/workspaces/${ctx.workspaceId}/onboarding/discovery-status`, { authToken: ctx.token ?? undefined });
        if (!alive) return;
        setStatus(data);
        if (!retriedRef.current) {
          const jobs = Object.values(data.jobs);
          const stalled = jobs.every((j) => j.status === 'pending' || j.status === 'running');
          const allPending = jobs.every((j) => j.status === 'pending');
          if (stalled) {
            stallRef.current++;
            if (stallRef.current >= (allPending ? 3 : 10)) {
              // "All pending" right after connect is the first mail sync still
              // landing, not a stall — restarting now scans an empty store and
              // freezes onboarding. Only restart once sync is ready (mail data
              // present); otherwise keep polling without consuming the one-shot.
              const ready = await apiFetch<{ isReady?: boolean }>(`/workspaces/${ctx.workspaceId}/onboarding/sync-status`, { authToken: ctx.token ?? undefined }).then((s) => s?.isReady === true).catch(() => false);
              if (ready) {
                retriedRef.current = true;
                apiFetch(`/workspaces/${ctx.workspaceId}/onboarding/restart-discovery`, { method: 'POST', authToken: ctx.token ?? undefined }).catch(() => {});
              } else {
                stallRef.current = (allPending ? 3 : 10) - 1; // re-check next tick
              }
            }
          }
          else stallRef.current = 0;
        }
      } catch { /* keep polling */ }
    };
    void poll();
    const id = setInterval(() => { void poll(); }, POLL_MS);
    return () => { alive = false; clearInterval(id); };
  }, [ctx.workspaceId, ctx.token]);

  const cal = status?.jobs.calendar, sent = status?.jobs.sent, inbox = status?.jobs.inbox;
  // A lane is "done" only when its real job has finished AND its paced reveal time
  // has elapsed — so the scan always takes ~5–9s, like a live Google sync.
  const calDone = !!cal && TERMINAL.has(cal.status) && revealed.cal;
  const sentDone = !!sent && TERMINAL.has(sent.status) && revealed.sent;
  const inboxDone = !!inbox && TERMINAL.has(inbox.status) && revealed.inbox;
  const scansDone = calDone && sentDone && inboxDone;

  // Under the hood: the instant the real jobs finish (independent of the paced
  // reveal animation above), persist the user's contacts + profile-to-Drive
  // silently. Fires once per mount; ChatBox also guards once per workspace.
  const dataReady = !!cal && TERMINAL.has(cal.status) && !!sent && TERMINAL.has(sent.status) && !!inbox && TERMINAL.has(inbox.status);
  const savedRef = useRef(false);
  useEffect(() => {
    if (!dataReady || savedRef.current) return;
    savedRef.current = true;
    ctx.onScanComplete?.(deriveBundle(status, ctx.userEmail, ctx.userName, ctx.isDemo));
  }, [dataReady]);

  // Chat-first: when the scan finishes, flow straight into the assessment — no
  // "Show me what you found" click. Fire once, only on the live beat.
  const advancedRef = useRef(false);
  useEffect(() => {
    if (!scansDone || !ctx.isCurrent || advancedRef.current) return;
    advancedRef.current = true;
    ctx.advance(ctx.isDemo ? 'assessment' : 'analysis', { bundle: deriveBundle(status, ctx.userEmail, ctx.userName, ctx.isDemo) });
  }, [scansDone, ctx.isCurrent]);

  // Real flow renders NOTHING — the scan is invisible plumbing (poll +
  // auto-advance); the analysis log presents everything it found. The lane
  // card survives only in demo (the demo path never runs the analysis log).
  if (!ctx.isCurrent || !ctx.isDemo) return null;

  return (
    <>
      <p className="oa-welcome-body">Loading Marcus's email and calendar…</p>
      <div className="obr-card obr-scan">
        <Lane icon="📅" label="Calendar" unit="events" count={(cal?.pastCount ?? 0) + (cal?.upcomingCount ?? 0)} done={calDone} />
        <Lane icon="📤" label="Sent" unit="people you write to" count={sent?.contactCount ?? 0} done={sentDone} />
        <Lane icon="📥" label="Inbox" unit="messages read" count={inbox?.emailCount ?? 0} done={inboxDone} />
      </div>
    </>
  );
}

const ASSESS_INTRO_REAL = [
  "Here is what I learned about you in a few seconds.",
];
const ASSESS_INTRO_DEMO = [
  "Watch this. From email alone, I can build a whole profile of a person. This is Marcus — but every read here, I'd be making about you. Honestly, yours would be more interesting.",
];

function AssessmentBeat({ payload, ctx }: { payload: BeatPayload; ctx: BeatContext }) {
  const b = payload.bundle;
  const [page, setPage] = useState(0);
  const [introDone, setIntroDone] = useState(!ctx.isCurrent);
  // The advance button only appears once the profile card has finished fading in
  // (its obr-fade animation ends). Past beats reveal immediately.
  const [revealed, setRevealed] = useState(!ctx.isCurrent);
  useRevealFallback(ctx.isCurrent && !revealed, () => setRevealed(true));
  if (!b) return null;
  const sections = b.assessSections ?? [];
  const PER_PAGE = 2;
  const pageCount = Math.max(1, Math.ceil(sections.length / PER_PAGE));
  const pageSections = sections.slice(page * PER_PAGE, page * PER_PAGE + PER_PAGE);
  return (
    <>
      <TypedLines active={ctx.isCurrent} className="obr-assess-intro" lines={ctx.isDemo ? ASSESS_INTRO_DEMO : ASSESS_INTRO_REAL} onComplete={() => setIntroDone(true)} />
      {introDone && (<>
      <div className="obr-card obr-assess obr-fade" onAnimationEnd={(e) => { if (e.target === e.currentTarget) setRevealed(true); }}>
        <div className="obr-assess-kind">AI Email Profile</div>
        <div className="obr-assess-head">
          <div className="obr-av" style={{ background: hueFor(ctx.userEmail || b.name) }}>{b.initial}</div>
          <div className="obr-assess-id"><div className="obr-assess-name">{b.name}</div>{b.company && <div className="obr-assess-co">{b.company}</div>}</div>
        </div>
        {sections.length > 0 ? (
          <div className="obr-assess-sections" key={page}>
            {pageSections.map((s) => (
              <div className="obr-assess-section" key={s.title}>
                <div className="obr-assess-section-title">{s.title}</div>
                <p className="obr-assess-section-body">{s.body}</p>
              </div>
            ))}
          </div>
        ) : b.assess.length > 0 ? (
          <div className="obr-assess-rows">
            {b.assess.map((r, i) => (
              <div className={`obr-assess-row${r.bold ? ' bold' : ''}`} key={i}><span className="obr-assess-ic">{AICON[r.icon]}</span><span>{r.text}</span></div>
            ))}
          </div>
        ) : null}
        {pageCount > 1 && (
          <div className="obr-assess-pager">
            <button type="button" className="obr-assess-arrow" disabled={page === 0} onClick={() => setPage((pp) => Math.max(0, pp - 1))} aria-label="Previous page">‹</button>
            <div className="obr-assess-dots">
              {Array.from({ length: pageCount }).map((_, i) => (
                <button type="button" key={i} className={`obr-assess-dot${i === page ? ' is-active' : ''}`} onClick={() => setPage(i)} aria-label={`Page ${i + 1}`} />
              ))}
            </div>
            <button type="button" className="obr-assess-arrow" disabled={page === pageCount - 1} onClick={() => setPage((pp) => Math.min(pageCount - 1, pp + 1))} aria-label="Next page">›</button>
          </div>
        )}
      </div>
      {revealed && <AdvanceBubble label="Okay, what else?" isCurrent={ctx.isCurrent} onAdvance={() => ctx.advance('calendar', { bundle: b })} />}
      </>)}
    </>
  );
}

const CALENDAR_INTRO_REAL = ["Here are some of the things I've noticed you're up to:"];
const CALENDAR_INTRO_DEMO = ["The calendar fills in the rest — the shape of someone's weeks. This is Marcus's. Yours is right there too, you know. I just can't see it yet."];

// Build "what you're up to" from REAL synced event OBJECTS only (source-filtered)
// — never the discovery-job bundle, which can replay a previous demo scan's results
// (recurring "Therapy — Dr. Reyes", "Travel — New York", etc.) that survive in the
// onboarding_jobs table across resets. The window reaches a bit back and a bit
// forward (last 7 / next 14 days) so a quiet week still shows the shape of what
// the user is up to, chronologically (#3509).
function realWeekEvents(items: any[]): AssessRow[] {
  const events = items.filter((o) => !isDemoSourced(o));
  const startOfToday = new Date(); startOfToday.setHours(0, 0, 0, 0);
  const now = startOfToday.getTime();
  const windowStart = now - 7 * 86_400_000;
  const windowEnd = now + 14 * 86_400_000;
  const dated = events
    .map((o) => ({ o, t: eventStartTs(String(pick(o, 'startAt') || pick(o, 'start') || '')) }))
    .filter(({ t }) => Number.isFinite(t) && t >= windowStart)
    .sort((a, b) => a.t - b.t);
  const inWindow = dated.filter(({ t }) => t <= windowEnd);
  // If the window is genuinely empty, fall back to the next few dated events so
  // the card is never blank. Dedup identical day+title so a doubled calendar
  // entry doesn't repeat; cap the list so a packed stretch stays scannable.
  const list = inWindow.length ? inWindow : dated.slice(0, 5);
  const seen = new Set<string>();
  const rows: AssessRow[] = [];
  for (const { o, t } of list) {
    const title = String(pick(o, 'title') ?? 'Event').trim();
    const day = new Date(t).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
    const key = `${day}|${title.toLowerCase()}`;
    if (seen.has(key)) continue;
    seen.add(key);
    rows.push({ icon: 'cal', text: `${day} — ${title}` });
    if (rows.length >= 8) break;
  }
  return rows;
}

function CalendarBeat({ payload, ctx }: { payload: BeatPayload; ctx: BeatContext }) {
  const b = payload.bundle;
  const [introDone, setIntroDone] = useState(!ctx.isCurrent);
  const [revealed, setRevealed] = useState(!ctx.isCurrent);
  useRevealFallback(ctx.isCurrent && !revealed, () => setRevealed(true));
  // Real path: derive rows from real synced event objects (null = still loading).
  const [realRows, setRealRows] = useState<AssessRow[] | null>(null);
  useEffect(() => {
    if (ctx.isDemo) return; // demo keeps the scripted Marcus bundle rows
    let alive = true;
    let tries = 0;
    void (async () => {
      while (alive && tries < 5) {
        const items = await fetchObjects(ctx, 'event', 400);
        if (items.length > 0) { if (alive) setRealRows(realWeekEvents(items)); return; }
        tries++;
        await new Promise((r) => setTimeout(r, 1500));
      }
      if (alive) setRealRows([]); // genuinely empty calendar — render gracefully
    })();
    return () => { alive = false; };
  }, []);
  // Real path: a calendar with one or zero events in view isn't worth a whole
  // beat — skip straight to contacts instead of showing a near-empty card.
  const skip = !ctx.isDemo && realRows !== null && realRows.length <= 1;
  useEffect(() => {
    if (skip && ctx.isCurrent) ctx.advance('contacts', { bundle: payload.bundle });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skip, ctx.isCurrent]);
  if (!b) return null;
  if (skip) return null;
  const loading = !ctx.isDemo && realRows === null;
  const rows = ctx.isDemo ? b.calRows : (realRows ?? []);
  return (
    <>
      <TypedLines active={ctx.isCurrent} className="obr-assess-intro" lines={ctx.isDemo ? CALENDAR_INTRO_DEMO : CALENDAR_INTRO_REAL} onComplete={() => setIntroDone(true)} />
      {introDone && (loading ? (
        <div className="obr-card obr-demo"><div className="obr-demo-cap"><span className="obr-spin" /> <span>reading your calendar…</span></div></div>
      ) : (<>
      {rows.length > 0 && (
        <div className="obr-card obr-assess obr-fade" onAnimationEnd={(e) => { if (e.target === e.currentTarget) setRevealed(true); }}>
          {!ctx.isDemo && <div className="obr-card-h">📅 What you're up to</div>}
          <div className="obr-assess-rows">
            {rows.map((r, i) => {
              const [lead, ...rest] = r.text.split(' — ');
              return <div className="obr-assess-row" key={i}><span className="obr-assess-ic">{AICON[r.icon]}</span><span><strong className="obr-assess-lead">{lead}</strong>{rest.length ? ` — ${rest.join(' — ')}` : ''}</span></div>;
            })}
          </div>
        </div>
      )}
      {/* Demo keeps its scripted closing; the real path goes straight to the card. */}
      {ctx.isDemo && (
        <p className="obr-assess-closing obr-fade">{b.calClosingBold}<strong>So I'll plan around your life, not over it.</strong></p>
      )}
      {revealed && <AdvanceBubble label="Keep going" isCurrent={ctx.isCurrent} onAdvance={() => ctx.advance('contacts', { bundle: b })} />}
      </>))}
    </>
  );
}

interface NetCard { id: string; name: string; email: string; note: string; aiEnhanced?: boolean }

const CONTACTS_INTRO_REAL = ['Here are some contacts I put together, you can add more details or let me enhance them for you.'];
const CONTACTS_INTRO_DEMO = ["I can surface a whole network from email alone — the people who actually matter. Here are Marcus's. I'd rather be showing you yours, but here we are."];

function ContactsBeat({ payload, ctx }: { payload: BeatPayload; ctx: BeatContext }) {
  const b = payload.bundle;
  const [introDone, setIntroDone] = useState(!ctx.isCurrent);
  const [revealed, setRevealed] = useState(!ctx.isCurrent);
  useRevealFallback(ctx.isCurrent && !revealed, () => setRevealed(true));
  // Render the REAL extracted contacts — they always have ids (so they're always
  // clickable) and carry richer notes than the scripted bundle, which can drift
  // out of sync with whatever the seed actually produced. Fall back to the
  // scripted list (inert) only while the real contacts are still loading. Retry,
  // since extraction may still be running when this beat first renders.
  const [real, setReal] = useState<NetCard[] | null>(null);
  useEffect(() => {
    let alive = true;
    let tries = 0;
    const attempt = async (): Promise<boolean> => {
      const items = await fetchObjects(ctx, 'contact', 500);
      // Dedup by normalized name — the same person can appear under two
      // addresses (e.g. a stale persona alias), which surfaced as two identical
      // cards. Keep the highest-emailCount instance (list is sorted desc).
      const seenNames = new Set<string>();
      const cards: NetCard[] = items
        .filter((o) => o.id && (pick(o, 'email') || pick(o, 'name') || pick(o, 'title')))
        .filter((o) => !NOTSERVICE.test(resolveStr(pick(o, 'email'))))
        .filter((o) => ctx.isDemo ? isDemoSourced(o) : !isDemoSourced(o))
        .sort((x, y) => Number(pick(y, 'emailCount') ?? 0) - Number(pick(x, 'emailCount') ?? 0))
        .filter((o) => {
          // Resolve each candidate to a string first — an object-shaped name
          // (`{ value }`) is truthy, so `pick(name) || pick(title)` would keep
          // the object and `String(object)` → "[object Object]", breaking dedup.
          const key = (resolveStr(pick(o, 'name')) || resolveStr(pick(o, 'title')) || resolveStr(pick(o, 'email'))).trim().toLowerCase();
          if (!key || seenNames.has(key)) return false;
          seenNames.add(key);
          return true;
        })
        .slice(0, 6)
        .map((o) => {
          const email = resolveStr(pick(o, 'email'));
          const company = resolveStr(pick(o, 'company')) || resolveStr(pick(o, 'firm'));
          const role = resolveStr(pick(o, 'role')) || resolveStr(pick(o, 'contactTitle'));
          let note = resolveStr(pick(o, 'summary')) || [role, company].filter(Boolean).join(' · ') || 'One of your key contacts.';
          if (note.length > 150) note = `${note.slice(0, 148)}…`;
          return {
            id: o.id,
            name: resolveStr(pick(o, 'name')) || resolveStr(pick(o, 'title')) || email,
            email,
            note,
            aiEnhanced: !!(pick(o, 'enriched') || pick(o, 'aiProfile')),
          };
        });
      if (cards.length === 0) return false;
      if (alive) setReal(cards);
      return true;
    };
    void (async () => {
      while (alive && tries < 6) {
        if (await attempt()) return;
        tries++;
        await new Promise((r) => setTimeout(r, 1500));
      }
    })();
    return () => { alive = false; };
  }, []);
  if (!b) return null;
  const cards: NetCard[] = real ?? b.contacts.map((c) => ({ id: '', name: c.name, email: c.email, note: c.note, aiEnhanced: c.aiEnhanced }));
  return (
    <>
      <TypedLines active={ctx.isCurrent} className="obr-assess-intro" lines={ctx.isDemo ? CONTACTS_INTRO_DEMO : CONTACTS_INTRO_REAL} onComplete={() => setIntroDone(true)} />
      {introDone && (<>
      <div className="obr-net-grid obr-fade">
        {cards.map((c) => (
          <div
            className={`obr-net-card${c.id ? ' clickable' : ''}`}
            key={c.id || c.email || c.name}
            role={c.id ? 'button' : undefined}
            tabIndex={c.id ? 0 : undefined}
            onClick={c.id ? () => ctx.onOpenContact?.(c.id) : undefined}
            onKeyDown={c.id ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); ctx.onOpenContact?.(c.id); } } : undefined}
          >
            <div className="obr-net-head">
              <div className="obr-av obr-net-av" style={{ background: hueFor(c.email || c.name) }}>{initials(c.name, c.email)}</div>
              <div className="obr-net-id"><div className="obr-net-name">{c.name}</div><div className="obr-net-email">{c.email}</div></div>
            </div>
            <div className="obr-net-note">{c.note}</div>
          </div>
        ))}
      </div>
      <p className="obr-assess-closing obr-fade" onAnimationEnd={(e) => { if (e.target === e.currentTarget) setRevealed(true); }}>Click any contact to open it — and hit <strong>AI enhancement</strong> to see how much is already out there.</p>
      {revealed && <AdvanceBubble label="Ok, but how do I use Xavior?" isCurrent={ctx.isCurrent} onAdvance={() => ctx.advance('demo', { which: 'agents', bundle: b })} />}
      </>)}
    </>
  );
}

// ── Self-contained demo experiences (no agent calls, no chat pollution) ──
interface DemoEmailMsg { threadId: string; fromName: string; fromEmail: string; subject: string; body: string; date: string; aiDraft?: string }

// Seed-accurate fallback so the email demo is NEVER empty, even if the live
// inbox fetch fails. Mirrors the demo-seed's most-recent received people email.
const FALLBACK_EMAIL: DemoEmailMsg = {
  threadId: 'demo-fallback',
  fromName: 'Mira Vale',
  fromEmail: 'mira@miravale.com',
  subject: 'shhh — keep Friday quiet',
  body: "Marcus, don't bring up the news at dinner Friday — it's still under wraps and it won't be just us at the table. Stick to the studio stuff? Also bringing someone you'll want to meet. xx M",
  date: 'Today, 1:14 PM',
  aiDraft: "Lips sealed — I won't breathe a word. I'll keep the conversation on the studio side and let you steer the rest. Looking forward to Friday, and happy to meet whoever you're bringing along. — Marcus",
};

const NOTSERVICE = /subscriptions|bills|notifications|newsletters|shopping|travel|transactional/i;
function extractInboxMessages(data: unknown): any[] {
  if (Array.isArray(data)) return data;
  const d = data as Record<string, any>;
  if (Array.isArray(d?.messages)) return d.messages;
  if (Array.isArray(d?.items)) return d.items;
  if (Array.isArray(d?.threads)) return d.threads;
  if (Array.isArray(d?.groups)) return d.groups.flatMap((g: any) => g.messages ?? g.items ?? []);
  return [];
}
function toDemoEmail(m: any): DemoEmailMsg | null {
  const fromEmail = m.fromEmail || m.from_email || '';
  const fromName = m.fromName || m.from_name || (fromEmail ? fromEmail.split('@')[0] : '');
  const subject = m.subject || '';
  if (!fromEmail || !fromName || !subject) return null;
  const body = (m.body || m.textBody || m.snippet || '').toString().replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  return {
    threadId: m.threadId || m.thread_id || m.id || 'demo',
    fromName, fromEmail, subject,
    body: body.slice(0, 360),
    date: m.dateLabel || m.date || 'Today',
    aiDraft: m.aiDraft || m.ai_draft || undefined,
  };
}

// Stream a contextual reply from /draft-reply, returning the full text. Falls
// back to the email's cached AI draft (or the seed fallback) on any failure.
async function draftReplyText(email: DemoEmailMsg): Promise<string> {
  try {
    const res = await fetch(`${emailApiBase()}/draft-reply`, {
      method: 'POST',
      headers: { ...getHeaders(), Accept: 'text/event-stream' },
      body: JSON.stringify({ threadId: email.threadId, subject: email.subject, to: email.fromEmail, messages: [{ from: email.fromEmail, body: email.body, date: email.date }], draftMode: 'xavior_ai' }),
    });
    let full = '';
    if (res.ok && res.body) {
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = '';
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const lines = buf.split('\n');
        buf = lines.pop() || '';
        for (const ln of lines) {
          if (!ln.startsWith('data: ')) continue;
          // Backend `/draft-reply` SSE contract: {delta} (incremental) then {done, draft} (final).
          try { const d = JSON.parse(ln.slice(6)); if (typeof d.draft === 'string' && d.draft) full = d.draft; else if (typeof d.delta === 'string') full += d.delta; } catch { /* partial */ }
        }
      }
    }
    if (full.trim()) return full.trim();
  } catch { /* fall through */ }
  return email.aiDraft || "Thanks for this — I'll take a look and follow up shortly.";
}

/**
 * Email beat: "What was the email from {sender}?" — answered with a clickable
 * mini email card (EmailResultCard). Clicking it opens a reply card
 * (EmailDraftCard, reply mode): Rewrite drafts a contextual reply, Send flips to
 * a "✓ SENT" confirmation. The send is simulated (no real mail in onboarding).
 */
function EmailDemo({ isDemo, onReady }: { isDemo?: boolean; onReady?: () => void }) {
  const [email, setEmail] = useState<DemoEmailMsg | null>(null);
  const [empty, setEmpty] = useState(false);
  const [replying, setReplying] = useState(false);
  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const data = await getCachedInbox({ limit: 20, offset: 0, grouped: false, category: 'humans' });
        const msgs = extractInboxMessages(data);
        const picked = msgs
          .map(toDemoEmail)
          .filter((e): e is DemoEmailMsg => !!e && !NOTSERVICE.test(e.fromEmail));
        if (alive && picked.length > 0) {
          if (isDemo) {
            // Demo: prefer the scripted Taylor Swift thread; fall back to the scripted
            // FALLBACK_EMAIL so real account emails never bleed into the demo flow.
            const preferred = picked.find((e) => /mira/i.test(e.fromName) || /mira/i.test(e.fromEmail));
            setEmail(preferred ?? FALLBACK_EMAIL);
          } else {
            setEmail(picked[0]);
          }
          return;
        }
      } catch { /* fall through */ }
      // Empty/failed fetch: ONLY the demo seed gets the scripted fallback. A real
      // account must never see fabricated content (#3509) — show a neutral state.
      if (alive) { if (isDemo) setEmail(FALLBACK_EMAIL); else setEmpty(true); }
    })();
    return () => { alive = false; };
  }, [isDemo]);

  if (empty) return <div className="obr-card obr-demo obr-fade" onAnimationEnd={(e) => { if (e.target === e.currentTarget) onReady?.(); }}><div className="obr-demo-cap">Your inbox is still syncing — I&apos;ll surface a real message here in a moment. You can ask me &quot;What&apos;s my latest email?&quot; anytime.</div></div>;
  if (!email) return <div className="obr-card obr-demo"><div className="obr-demo-cap"><span className="obr-spin" /> <span>opening your inbox…</span></div></div>;

  const cardData: EmailCardData = {
    messageId: email.threadId,
    threadId: email.threadId,
    fromName: email.fromName,
    fromEmail: email.fromEmail,
    subject: email.subject,
    snippet: email.body,
    date: email.date,
    isUnread: true,
  };
  const replyData: EmailDraftCardData = {
    to: email.fromEmail,
    toName: email.fromName,
    subject: `Re: ${email.subject}`,
    body: '',
    replyMode: true,
    threadId: email.threadId,
  };

  return (
    <>
      {!replying ? (
        <div className="obr-fade" onAnimationEnd={(e) => { if (e.target === e.currentTarget) onReady?.(); }}><EmailResultCard data={cardData} onOpen={() => setReplying(true)} /></div>
      ) : (
        <>
          <EmailDraftCard
            data={replyData}
            onCompose={async () => ({ body: await draftReplyText(email) })}
            onSend={async () => true}
          />
          <button type="button" className="obr-link-btn" onClick={() => setReplying(false)} aria-label="Close reply">✕ Close reply</button>
        </>
      )}
    </>
  );
}

// fetchObjects, pick, isDemoSourced, eventStartTs — imported from onboarding-beats-shared

// Event beat: "When was my therapist appointment?" — answered with the real
// calendar event (prefers the scripted therapy appointment on the demo seed),
// rendered as the schedule result card.

function EventDemo({ ctx, onReady }: { ctx: BeatContext; onReady?: () => void }) {
  const [events, setEvents] = useState<EventsCardEvent[] | null>(null);
  const [done, setDone] = useState(false);
  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const items = await fetchObjects(ctx, 'event', 400);
        const scoped = items.filter((o) => ctx.isDemo ? isDemoSourced(o) : !isDemoSourced(o));
        const toCard = (o: any): EventsCardEvent => ({
          id: o.id,
          objectId: o.id,
          title: pick(o, 'title') || 'Event',
          start: pick(o, 'startAt') || pick(o, 'startTime') || pick(o, 'start') || '',
          end: pick(o, 'endAt') || pick(o, 'endTime') || pick(o, 'end') || undefined,
          allDay: !!pick(o, 'allDay'),
          location: pick(o, 'location') || undefined,
        });
        let chosen: EventsCardEvent[] = [];
        if (ctx.isDemo) {
          // Demo answers "When's my next therapist appointment?" — the one event.
          const match = scoped.find((o) => /therap|reyes/i.test(`${pick(o, 'title') ?? ''} ${pick(o, 'location') ?? ''}`)) ?? scoped[0];
          if (match) chosen = [toCard(match)];
        } else {
          // Real path answers "What's on my calendar this week?" — ONLY this
          // week's events (next 7 days). Don't reach beyond, or the "this week"
          // framing is a lie. A genuinely empty week falls back to the next few
          // upcoming (labelled "Coming up" below) so the beat is never blank.
          const startOfToday = new Date(); startOfToday.setHours(0, 0, 0, 0);
          const weekEnd = startOfToday.getTime() + 7 * 86_400_000;
          const upcoming = scoped
            .map((o) => ({ o, t: eventStartTs(String(pick(o, 'startAt') || pick(o, 'start') || '')) }))
            .filter(({ t }) => Number.isFinite(t) && t >= startOfToday.getTime())
            .sort((a, b) => a.t - b.t);
          const thisWeek = upcoming.filter(({ t }) => t <= weekEnd);
          chosen = (thisWeek.length ? thisWeek.slice(0, 8) : upcoming.slice(0, 4)).map(({ o }) => toCard(o));
        }
        if (alive && chosen.length) setEvents(chosen);
      } catch { /* fall through */ }
      if (alive) setDone(true);
    })();
    return () => { alive = false; };
  }, []);

  if (!events || events.length === 0) {
    if (!done) return <div className="obr-card obr-demo"><div className="obr-demo-cap"><span className="obr-spin" /> <span>checking your calendar…</span></div></div>;
    // Real account whose events haven't synced yet (or an empty calendar): never
    // render a blank beat — show a graceful state and let the tour continue (#3512).
    return <div className="obr-card obr-demo obr-fade" onAnimationEnd={(e) => { if (e.target === e.currentTarget) onReady?.(); }}><div className="obr-demo-cap">Your calendar&apos;s quiet right now. Once events sync in, ask me &quot;What&apos;s on my calendar this week?&quot; and I&apos;ll pull them up.</div></div>;
  }
  const starts = events.map((e) => e.start).filter(Boolean).sort();
  // Label "This week" only when the last event actually falls within 7 days;
  // otherwise the card reaches further out, so call it "Coming up".
  const lastTs = eventStartTs(String(starts[starts.length - 1] ?? events[0].start));
  const withinWeek = lastTs <= new Date().setHours(0, 0, 0, 0) + 7 * 86_400_000;
  const data: EventsCardData = ctx.isDemo
    ? { rangeLabel: 'Therapy', rangeStart: events[0].start, rangeEnd: events[0].end || events[0].start, events }
    : { rangeLabel: withinWeek ? 'This week' : 'Coming up', rangeStart: starts[0] ?? events[0].start, rangeEnd: starts[starts.length - 1] ?? events[0].start, events };
  return (
    <div className="obr-fade" onAnimationEnd={(e) => { if (e.target === e.currentTarget) onReady?.(); }}>
      <EventsResultCard data={data} onOpenEvent={(e) => { if (ctx.isDemo) calendarFlash.request('2026-07-07'); ctx.onOpenEvent?.(e.id); }} onOpenRange={() => { if (ctx.isDemo) calendarFlash.request('2026-07-07'); ctx.onOpenEvent?.(); }} hideStrip hideOpenButton />
    </div>
  );
}

// Contact beat: "What's {name}'s contact info?" — answered with the real contact
// object (prefers Dario on the demo seed), rendered as the single-contact card.
const CONTACT_DEMO_INTRO_REAL = ["This is the person you email most. And I can pull up anyone like this — by name, company, or any half-remembered detail — just ask."];
const CONTACT_DEMO_INTRO_DEMO = ["Here's the person Marcus emails most. And I can pull up anyone like this — by name, company, or a half-remembered detail. (On your account, this'd be your people. Just saying.)"];

function ContactDemo({ ctx, onReady }: { ctx: BeatContext; onReady?: () => void }) {
  const [contact, setContact] = useState<ContactCardData['contacts'][number] | null>(null);
  const [done, setDone] = useState(false);
  const [introDone, setIntroDone] = useState(!ctx.isCurrent);
  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const items = await fetchObjects(ctx, 'contact', 500);
        const scoped = items.filter((o) => ctx.isDemo ? isDemoSourced(o) : !isDemoSourced(o));
        // Both paths answer "Who do I email most?" — the highest email-count
        // contact (so the card always matches the question, real or demo).
        const match = [...scoped].sort((a, b) => Number(pick(b, 'emailCount') ?? 0) - Number(pick(a, 'emailCount') ?? 0))[0] ?? scoped[0];
        if (alive && match) {
          setContact({
            id: match.id,
            name: pick(match, 'name') || pick(match, 'title') || pick(match, 'email') || 'Contact',
            email: pick(match, 'email') || pick(match, 'primaryEmail') || undefined,
            title: pick(match, 'jobTitle') || pick(match, 'role') || pick(match, 'firm') || undefined,
            company: pick(match, 'company') || pick(match, 'organization') || pick(match, 'firm') || undefined,
            photoUrl: pick(match, 'photoUrl') || pick(match, 'avatarUrl') || undefined,
            bio: pick(match, 'bio') || pick(match, 'aiSummary') || pick(match, 'summary') || undefined,
          });
        }
      } catch { /* fall through */ }
      if (alive) setDone(true);
    })();
    return () => { alive = false; };
  }, []);

  if (!contact) {
    if (!done) return <div className="obr-card obr-demo"><div className="obr-demo-cap"><span className="obr-spin" /> <span>looking up your contacts…</span></div></div>;
    // Never a blank beat — graceful state while contacts are still being extracted (#3512).
    return <div className="obr-card obr-demo obr-fade" onAnimationEnd={(e) => { if (e.target === e.currentTarget) onReady?.(); }}><div className="obr-demo-cap">I&apos;m still surfacing the people from your inbox. In a moment, ask me &quot;Who do I email most?&quot; and I&apos;ll show your key contacts.</div></div>;
  }
  return (
    <>
      <TypedLines active={ctx.isCurrent} className="obr-assess-intro" lines={ctx.isDemo ? CONTACT_DEMO_INTRO_DEMO : CONTACT_DEMO_INTRO_REAL} onComplete={() => setIntroDone(true)} />
      {introDone && <div className="obr-fade" onAnimationEnd={(e) => { if (e.target === e.currentTarget) onReady?.(); }}><ContactDetailCard contact={contact} onOpen={() => ctx.onOpenContact?.(contact.id)} /></div>}
    </>
  );
}

// Drive beat: frames the integrated Drive as a memory Xavior WRITES to (not a
// reader of the user's pre-existing files). Real path surfaces the profile doc
// Xavior just wrote during onboarding ("Your XAVIOR profile") — it always exists
// and is always visible to us, so it reliably demonstrates the write capability.
const DRIVE_DEMO_INTRO_DEMO = ["I come with a fully integrated Drive — folders, docs, a real memory I can write to and pull from on command. Marcus has been working a hire, so let's find his candidate doc by name. (Imagine this full of your stuff instead.)"];
const DRIVE_DEMO_INTRO_REAL = ["I don't just read your world — I can write to it. I come with a built-in Drive: a memory I save things to and pull back on command. While we talked, I wrote up everything I learned about you. Here's the doc I just saved for you:"];

function DriveDemo({ ctx, onReady }: { ctx: BeatContext; onReady?: () => void }) {
  const [note, setNote] = useState<NoteCardData | null>(null);
  const [done, setDone] = useState(false);
  const [introDone, setIntroDone] = useState(!ctx.isCurrent);
  useEffect(() => {
    let alive = true;
    let tries = 0;
    const attempt = async (): Promise<boolean> => {
      const items = await fetchObjects(ctx, 'note', 200);
      const scoped = items.filter((o) => ctx.isDemo ? isDemoSourced(o) : !isDemoSourced(o));
      // Demo: prefer the sample candidate doc. Real: surface the profile doc we
      // wrote this session ("Your XAVIOR profile") — never imply we can read the
      // user's own pre-existing files. Fall back to the most recent note we wrote.
      const match = ctx.isDemo
        ? (scoped.find((o) => /resume|candidate|hir(e|ing)|role|interview|offer|case/i.test(String(pick(o, 'title') ?? ''))) ?? scoped[0])
        : (scoped.find((o) => /xavior profile|your profile|profile/i.test(String(pick(o, 'title') ?? ''))) ?? scoped[0]);
      if (match) {
        const raw = String(pick(match, 'body') || pick(match, 'bodyPreview') || pick(match, 'snippet') || '');
        const preview = raw.replace(/<[^>]+>/g, ' ').replace(/[#*_>`]/g, '').replace(/\s+/g, ' ').trim();
        if (alive) setNote({ noteId: match.id, title: pick(match, 'title') || 'Note', bodyPreview: preview.slice(0, 160) });
        return true;
      }
      return false;
    };
    void (async () => {
      // The profile doc is written fire-and-forget right after the scan, so on the
      // real path it may not be queryable yet when this beat first renders — retry.
      while (alive && tries < 6) {
        try { if (await attempt()) { if (alive) setDone(true); return; } } catch { /* retry */ }
        tries++;
        await new Promise((r) => setTimeout(r, 1500));
      }
      if (alive) setDone(true);
    })();
    return () => { alive = false; };
  }, []);
  return (
    <>
      <TypedLines active={ctx.isCurrent} className="obr-assess-intro" lines={ctx.isDemo ? DRIVE_DEMO_INTRO_DEMO : DRIVE_DEMO_INTRO_REAL} onComplete={() => setIntroDone(true)} />
      {introDone && (!note ? (
        !done ? <div className="obr-card obr-demo obr-fade"><div className="obr-demo-cap"><span className="obr-spin" /> <span>opening your Drive…</span></div></div>
          : <div className="obr-card obr-demo obr-fade" onAnimationEnd={(e) => { if (e.target === e.currentTarget) onReady?.(); }}><div className="obr-demo-cap">I&apos;m just finishing writing that up — it&apos;ll be in your Drive in a moment. Anytime, ask me to &quot;Write a doc about …&quot; and I&apos;ll save it here for you.</div></div>
      ) : (
        <div className="obr-fade" onAnimationEnd={(e) => { if (e.target === e.currentTarget) onReady?.(); }}>
          <NoteResultCard data={note} onOpen={() => ctx.onOpenNote?.(note.noteId)} />
        </div>
      ))}
    </>
  );
}

// Skill demos are numbered 1..4; the Pages beat is the 5th/finale.
// One concrete "here's how to use this in XAVIOR" tip per demo beat, shown after
const DEMO_GUIDANCE: Record<string, (c: string) => string> = {
  agents:   ()  => ``,
  email:    (c) => `Try it yourself: ask me "What's the latest from ${c}?" or "Draft a reply" — I find it and draft, you send.`,
  calendar: (c) => `Ask me "What's on my calendar this week?" or "When's my next meeting with ${c}?"`,
  contacts: (c) => `Ask "Show me ${c}" or "Who do I email most?" — I surface people straight from your inbox.`,
  drive:    ()  => `Ask me to "Write a doc about …" or "Find my doc on …" — everything I write is saved to your Drive.`,
};

// ── Agent demo — combined "day at a glance" + pre-meeting research brief ──────
interface DayBrief {
  event: { title: string; time: string; with?: string };
  contact: { name: string; role: string; context: string } | null;
  action: { person: string; note: string };
}

const DEMO_DAY_BRIEF: DayBrief = {
  event: {
    title: 'Q2 Board Prep',
    time: 'Today · 3:00 PM, 2 hrs',
    with: 'Eva Lindgren',
  },
  contact: {
    name: 'Eva Lindgren',
    role: 'CEO, Northwind',
    context: "Your last thread covered compute costs and the inference roadmap — both likely on today's agenda.",
  },
  action: {
    person: 'Mira Vale',
    note: 'Still waiting on your reply about Friday dinner. Her last message was 3 days ago.',
  },
};

const AGENTS_DEMO_INTRO_REAL = ["Obviously, I can help you with your calendar, email, and contacts — but I can do even more. You can assign tasks to me, ask me to do research, and I've already started a daily briefing report for you — here is a preview:"];
const AGENTS_DEMO_INTRO_DEMO = ["Here's the part you're missing out on. I can run agents — like a daily briefing that lands in your feed every morning at 7am before you've even opened your laptop. This one's built on Marcus's day. Picture it built on yours."];

function AgentsDemo({ ctx, onReady }: { ctx: BeatContext; onReady?: () => void }) {
  const [introDone, setIntroDone] = useState(!ctx.isCurrent);
  // Real path renders the ACTUAL Daily Briefing (BriefingPanel self-fetches —
  // the same card the agent's briefing tool and the header dock show), so the
  // preview is the real product, not a hand-built imitation. The scripted
  // Marcus brief remains for the demo path only.
  const brief = ctx.isDemo ? DEMO_DAY_BRIEF : null;

  const ROW = ({ label, icon, children }: { label: string; icon: string; children: React.ReactNode }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10.5, fontWeight: 700, letterSpacing: '0.07em', color: 'var(--color-text-muted)' }}>
        <span>{icon}</span><span>{label}</span>
      </div>
      {children}
    </div>
  );

  return (
    <>
      <TypedLines active={ctx.isCurrent} className="obr-assess-intro" lines={ctx.isDemo ? AGENTS_DEMO_INTRO_DEMO : AGENTS_DEMO_INTRO_REAL} onComplete={() => setIntroDone(true)} />
      {introDone && (<>
      {!brief ? (
        <div className="obr-card obr-fade">
          <div className="obr-card-h">☀️ Daily Briefing</div>
          <BriefingPanel showRefresh={false} showWatchlist={false} />
        </div>
      ) : (
        <div className="obr-card obr-fade">
          <div className="obr-card-h">☀️ Daily Briefing</div>
          <div style={{ padding: '14px 16px 16px', display: 'flex', flexDirection: 'column', gap: 16 }}>
            <ROW label="NEXT UP" icon="📅">
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-text)' }}>{brief.event.title}</div>
              <div style={{ fontSize: 12.5, color: 'var(--color-text-muted)' }}>{brief.event.time}{brief.event.with ? ` · with ${brief.event.with}` : ''}</div>
            </ROW>
            {brief.contact && (<>
            <div style={{ borderTop: '1px solid var(--color-border-soft, #f0f1ec)' }} />
            <ROW label={ctx.isDemo ? 'BEFORE YOU WALK IN' : 'SOMEONE YOU SHOULD KNOW'} icon="👤">
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-text)' }}>{brief.contact.name}</div>
              {brief.contact.role && <div style={{ fontSize: 12.5, color: 'var(--color-text-muted)' }}>{brief.contact.role}</div>}
              <div style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--color-text-subtle, #2d2f31)', marginTop: 2 }}>{brief.contact.context}</div>
            </ROW>
            </>)}
            <div style={{ borderTop: '1px solid var(--color-border-soft, #f0f1ec)' }} />
            <ROW label="NEEDS YOUR ATTENTION" icon="⚡">
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-text)' }}>{brief.action.person}</div>
              <div style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--color-text-subtle, #2d2f31)' }}>{brief.action.note}</div>
            </ROW>
          </div>
        </div>
      )}
      <div className="obr-walk-closing obr-fade">Set this up as a daily agent and it lands here every morning — before your first meeting.</div>
      <div className="obr-assess-intro obr-fade" onAnimationEnd={(e) => { if (e.target === e.currentTarget) onReady?.(); }}>Let&apos;s go through some of my capabilities — I&apos;m very good at finding things.</div>
      </>)}
    </>
  );
}

function DemoBeat({ payload, ctx }: { payload: BeatPayload; ctx: BeatContext }) {
  const which = payload.which ?? 'agents';
  const idx = DEMO_ORDER.indexOf(which);
  const next = DEMO_ORDER[idx + 1];
  // The next-question button only appears once the demo's result has fully faded
  // in (each demo fires onReady on its card's animationend). Past beats reveal now.
  const [ready, setReady] = useState(!ctx.isCurrent);
  const onReady = () => setReady(true);
  useRevealFallback(ctx.isCurrent && !ready, () => setReady(true));
  // After the last demo (drive) there's no Pages step — its button flows straight
  // into the "your turn / connect" finale. (Previously this auto-advanced on
  // mount, which skipped the drive demo's own content — the candidate doc — so
  // the user never saw it. Now drive shows its note + a button like the others.)
  const goNext = () => next
    ? ctx.advance('demo', { which: next, bundle: payload.bundle })
    : ctx.advance('walkthrough', { bundle: payload.bundle });
  return (
    <>
      {/* Phase break: the skills section opens with its own header, cleanly
          separating "what I learned about you" from "how to use Xavior". */}
      {which === 'agents' && <div className="obr-section-h">How to use Xavior</div>}
      {which === 'agents'   && <AgentsDemo ctx={ctx} onReady={onReady} />}
      {which === 'email'    && <EmailDemo isDemo={ctx.isDemo} onReady={onReady} />}
      {which === 'calendar' && <EventDemo ctx={ctx} onReady={onReady} />}
      {which === 'contacts' && <ContactDemo ctx={ctx} onReady={onReady} />}
      {which === 'drive'    && <DriveDemo ctx={ctx} onReady={onReady} />}
      {which !== 'agents' && ready && (
        <div className="obr-walk-closing obr-fade">{DEMO_GUIDANCE[which]((payload.bundle?.topContact || '').trim() || 'someone')}</div>
      )}
      {ready && <AdvanceBubble label={(ctx.isDemo ? DEMO_NEXT_LABEL : REAL_NEXT_LABEL)[which]} isCurrent={ctx.isCurrent} onAdvance={goNext} />}
    </>
  );
}

// Final wrap-up: a separator, then how to remove the demo data (reversible),
// a confident next-action statement, and a secondary Teams upsell.

// 6/6: showcase the reports / web pages Xavior generates.
function PagesBeat({ payload, ctx }: { payload: BeatPayload; ctx: BeatContext }) {
  return (
    <>
      <div className="obr-assess-intro">
        I don&apos;t just read your world — I build from it: reports, briefings, and shareable web pages,
        generated from your data. I&apos;ve already drafted a couple for you.
      </div>
      <div className="obr-card">
        <div className="obr-card-h">📊 Reports &amp; pages</div>
        <p className="obr-mail-body">
          Open Pages to see what I made — a read on your world, and a breakdown of your subscriptions. Ask me for a
          report on anything — a client, a project, your week — and I&apos;ll generate the page.
        </p>
      </div>
      <AdvanceBubble label="Try it yourself" isCurrent={ctx.isCurrent} onAdvance={() => ctx.advance('walkthrough', { bundle: payload.bundle })} />
    </>
  );
}

// Finale. Real path: a confident wrap-up that simply unlocks the chat (no button
// — onboarding completes itself once the message lands). Demo path: the sassy
// re-offer — that was all Marcus, now do it for real — with a renewed connect
// prompt and a quiet "maybe later" to bail into the app.
const FINAL_PROMPT = 'What needs my attention today?';

function WalkthroughBeat({ ctx }: { payload: BeatPayload; ctx: BeatContext }) {
  // Hook must run unconditionally (rules-of-hooks) — used only on the demo path.
  const { remove: removeDemoData, busy: clearingDemo } = useDemoData();
  const real = !ctx.isDemo;
  if (real) {
    // Real finale: a tappable suggested-prompt button as the LAST beat. Tapping it
    // completes onboarding AND sends the question as the first real message in THIS
    // SAME conversation (ctx.runPrompt → activeConversation.id) — the onboarding
    // transcript becomes the user's live chat, never a brand-new one. Sending the
    // prompt before completing keeps both calls bound to the onboarding conversation.
    const onFinish = () => { ctx.runPrompt(FINAL_PROMPT); ctx.onComplete(); };
    return (
      <div className="oa-welcome">
        <p className="oa-welcome-body">
          And that&apos;s you — your week, your people, your inbox, all in one place. Everything I just walked you
          through is your real data.
        </p>
        <p className="oa-welcome-body">
          You&apos;re all good to go. Ask me anything — here&apos;s a good place to start:
        </p>
        {ctx.isCurrent && (
          <button type="button" className="oa-next-btn obr-fade" onClick={onFinish}>{FINAL_PROMPT}</button>
        )}
      </div>
    );
  }
  return (
    <div className="oa-welcome">
      <p className="oa-welcome-body">
        So. That was all Marcus — his week, his people, his inbox. Pretty good, right? Now picture every bit of
        that built on <em>your</em> world instead of a stranger&apos;s.
      </p>
      <p className="oa-welcome-body">
        I&apos;ll be honest: I&apos;ve been working with one hand tied behind my back this whole time. Connect your
        account and let me show you what I can really do.
      </p>
      <div className="oa-source-stack">
        <button className="oa-src-btn oa-src-btn--google" onClick={() => ctx.connect('oauth.google.email')}>
          <svg className="oa-src-ic" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path fill="#fff" d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z" /></svg>
          <span>Connect Google</span>
        </button>
        <button className="oa-src-btn oa-src-btn--microsoft" onClick={() => ctx.connect('oauth.microsoft.outlook')}>
          <svg className="oa-src-ic" viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true"><rect x="3" y="3" width="8" height="8" rx="1" /><rect x="13" y="3" width="8" height="8" rx="1" /><rect x="3" y="13" width="8" height="8" rx="1" /><rect x="13" y="13" width="8" height="8" rx="1" /></svg>
          <span>Connect Microsoft</span>
        </button>
      </div>
      <button type="button" className="oa-skip-link" onClick={() => ctx.onComplete()}>Maybe later — just let me in</button>
      {/* Clear the demo dataset and drop straight into a clean workspace. Same
          purge as the navbar pill + the clear_demo_data agent tool. */}
      <button
        type="button"
        className="oa-skip-link"
        disabled={clearingDemo}
        onClick={async () => { await removeDemoData().catch(() => {}); ctx.onComplete(); }}
      >
        {clearingDemo ? 'Clearing demo data…' : 'Clear demo data & start fresh'}
      </button>
    </div>
  );
}
